import {
    Alert,
    Box,
    Card,
    Checkbox,
    Form,
    FormButtonRow,
    FormGroup,
    FormParagraph,
    FormSection,
    IconButton,
    Link,
    Radio,
    Text,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type {
    IEmployment,
    IServiceConsumer,
    IServiceGroupWithEmailNotificationSettings,
    IServiceProvider,
    IServiceSupervisor,
    IServiceWithEmailNotificationSettings,
    Section,
} from '@common/interfaces/serviceGroupsWithEmailNotificationSettingsForEmployment';
import type { DefaultLanguage } from '@common/translate/translate';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization, ServiceCode, ServiceType } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { never } from '@common/utils/never';
import { serviceConsumerEmailNotificationPreferencesSchema } from '@common/validation/serviceConsumer';
import { serviceProviderEmailNotificationPreferencesSchema } from '@common/validation/serviceProvider';
import { serviceSupervisorEmailNotificationPreferencesSchema } from '@common/validation/serviceSupervisor';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization, validate } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';

interface IServiceEmailNotificationServiceGroupSettingsFormProps {
    employmentId: string;
    isReadOnly: boolean;
    onCancelled: () => void;
    onSaved: () => void;
    serviceEmailNotificationSettingsForGroup: IServiceGroupWithEmailNotificationSettings;
}

export function ServiceEmailNotificationServiceGroupSettingsForm(
    props: IServiceEmailNotificationServiceGroupSettingsFormProps
) {
    const authenticatedUserSelectedEmploymentId = useSelector(
        (state: IReduxState) => state.authentication.selectedEmploymentId
    );

    const isManager = hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit);
    const isOrganizationAddEditManager = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const isOwnProfile = authenticatedUserSelectedEmploymentId === props.employmentId;
    const canEdit = isOwnProfile || isManager;

    const [hasEmploymentEmailAddress, setHasEmploymentEmailAddress] = useState(false);
    const [hasEmploymentEmailAddressAlt, setHasEmploymentEmailAddressAlt] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [updatedServiceRelationIds, setUpdatedServiceRelationIds] = useState<string[]>([]);
    const [updatedServiceConsumers, setUpdatedServiceConsumers] = useState<IServiceConsumer[]>(
        initializeServiceItem('serviceConsumer')
    );
    const [updatedServiceProviders, setUpdatedServiceProviders] = useState<IServiceProvider[]>(
        initializeServiceItem('serviceProvider')
    );
    const [updatedServiceSupervisors, setUpdatedServiceSupervisors] = useState<IServiceSupervisor[]>(
        initializeServiceItem('serviceSupervisor')
    );

    const abortableFetch = useAbortableFetch();

    const employment: IEmployment = props.serviceEmailNotificationSettingsForGroup.employment;
    const language = getSelectedLanguage();

    useEffect(() => {
        setUpdatedServiceConsumers(initializeServiceItem('serviceConsumer'));
        setUpdatedServiceProviders(initializeServiceItem('serviceProvider'));
        setUpdatedServiceSupervisors(initializeServiceItem('serviceSupervisor'));
        setHasEmploymentEmailAddress(Boolean(employment.emailAddress));
        setHasEmploymentEmailAddressAlt(Boolean(employment.emailAddressAlt));
    }, [props.serviceEmailNotificationSettingsForGroup]);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !isEditing && canEdit && !props.isReadOnly ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : undefined
                }
                title={props.serviceEmailNotificationSettingsForGroup.name}
            />
            <Card.Body>
                <Form submitOnEnter={false}>
                    {isEditing && !props.isReadOnly && (
                        <>
                            {!hasEmploymentEmailAddress && !hasEmploymentEmailAddressAlt && (
                                <Alert appearance="info">
                                    {
                                        language.personalSettings.notifications[
                                            isManager ? 'workEmailNotSetManager' : 'workEmailNotSet'
                                        ]
                                    }
                                    &nbsp;
                                    <Alert.Link
                                        to={`/employments/${props.employmentId}/personalSettings?editForm=contactDetails&focusField=emailAddress`}
                                        variant="link"
                                    >
                                        {
                                            language.personalSettings.notifications[
                                                isManager ? 'setWorkEmailAccountManager' : 'setWorkEmail'
                                            ]
                                        }
                                    </Alert.Link>
                                </Alert>
                            )}
                            {hasEmploymentEmailAddress !== hasEmploymentEmailAddressAlt && (
                                <Alert appearance="info">
                                    {
                                        language.personalSettings.notifications[
                                            isManager ? 'addAnotherWorkEmailAccountManager' : 'addAnotherWorkEmail'
                                        ]
                                    }
                                    &nbsp;
                                    <Alert.Link
                                        to={`/employments/${props.employmentId}/personalSettings?editForm=contactDetails&focusField=emailAddressAlt`}
                                        variant="link"
                                    >
                                        {language.personalSettings.notifications.setWorkEmail}
                                    </Alert.Link>
                                </Alert>
                            )}
                        </>
                    )}

                    {props.serviceEmailNotificationSettingsForGroup.services.map((service) => {
                        const serviceRender: JSX.Element[] = [];
                        if (service.serviceConsumer) {
                            const noEmailOrNotifications = !checkNotificationSettingsForServiceRelation(
                                service.serviceConsumer
                            );
                            const consumer = updatedServiceConsumers.find(
                                (updatedConsumer) => updatedConsumer.id === service.serviceConsumer!.id
                            );
                            serviceRender.push(
                                renderFormSection<IServiceConsumer>(
                                    'consumer',
                                    service,
                                    consumer!,
                                    noEmailOrNotifications
                                )
                            );
                        }
                        if (service.serviceProvider) {
                            const noEmailOrNotifications = !checkNotificationSettingsForServiceRelation(
                                service.serviceProvider
                            );
                            const provider = updatedServiceProviders.find(
                                (updatedProvider) => updatedProvider.id === service.serviceProvider!.id
                            );
                            serviceRender.push(
                                renderFormSection<IServiceProvider>(
                                    'provider',
                                    service,
                                    provider!,
                                    noEmailOrNotifications
                                )
                            );
                        }
                        if (service.serviceSupervisor) {
                            const noEmailOrNotifications = !checkNotificationSettingsForServiceRelation(
                                service.serviceSupervisor
                            );
                            const supervisor = updatedServiceSupervisors.find(
                                (updatedSupervisor) => updatedSupervisor.id === service.serviceSupervisor!.id
                            );
                            serviceRender.push(
                                renderFormSection<IServiceSupervisor>(
                                    'supervisor',
                                    service,
                                    supervisor!,
                                    noEmailOrNotifications
                                )
                            );
                        }

                        return serviceRender;
                    })}

                    {isEditing && (
                        <FormButtonRow>
                            <CancelButton
                                disabled={isSaving}
                                onCancel={async () => handleCancel()}
                            />
                            <SaveButton
                                disabled={isSaving}
                                onSave={async () => await handleSubmit()}
                            />
                        </FormButtonRow>
                    )}
                </Form>
            </Card.Body>
        </Card>
    );

    function renderFormSection<T extends IServiceConsumer | IServiceProvider | IServiceSupervisor>(
        section: Section,
        service: IServiceWithEmailNotificationSettings,
        serviceRelation: T,
        noEmailOrNotifications: boolean
    ) {
        const serviceOrganizationSettings = service.serviceOrganizationSettings;
        const emailSettingTitle = getEmailSettingTitle(service, section);
        const sendNotificationOrganizationInbox =
            service.serviceOrganizationSettings?.sendNotificationOrganizationInbox;

        return (
            <FormSection
                key={serviceRelation.id + service.id}
                title={emailSettingTitle}
            >
                {serviceOrganizationSettings && sendNotificationOrganizationInbox && (
                    <>
                        <Text mb={4}>
                            {!isManager && language.personalSettings.notifications.settingsDeterminedByOrganization}

                            {isManager &&
                                !isOrganizationAddEditManager &&
                                language.personalSettings.notifications.settingsDeterminedByOrganizationManager}

                            {isManager && isOrganizationAddEditManager && (
                                <>
                                    {
                                        language.personalSettings.notifications
                                            .settingsDeterminedByOrganizationManagerEdit
                                    }

                                    <Link
                                        to={`/organizations/${serviceOrganizationSettings.organization.id}/view/serviceEmailNotifications?selectedCategoryId=${serviceOrganizationSettings.service.episodeCategoryId}&serviceGroupToEdit=${props.serviceEmailNotificationSettingsForGroup.id}`}
                                        variant="link"
                                    >
                                        {
                                            language.personalSettings.notifications
                                                .emailNotificationSettingAtOrganizationalLevel
                                        }
                                    </Link>
                                </>
                            )}
                        </Text>
                        <FormGroup label={language.personalSettings.notifications.receiveOn}>
                            {!serviceOrganizationSettings ||
                                (!serviceOrganizationSettings.emailAddressOrganizationInbox &&
                                    !serviceOrganizationSettings.emailAddressOrganizationInboxAlt &&
                                    !serviceOrganizationSettings.organization.emailAddressRequestInbox && (
                                        <FormParagraph>-</FormParagraph>
                                    ))}
                            {serviceOrganizationSettings && (
                                <>
                                    {serviceOrganizationSettings.emailAddressOrganizationInbox && (
                                        <FormParagraph>
                                            {serviceOrganizationSettings.emailAddressOrganizationInbox}
                                        </FormParagraph>
                                    )}
                                    {serviceOrganizationSettings.emailAddressOrganizationInboxAlt && (
                                        <FormParagraph>
                                            {serviceOrganizationSettings.emailAddressOrganizationInboxAlt}
                                        </FormParagraph>
                                    )}
                                    {serviceOrganizationSettings.organization &&
                                        serviceOrganizationSettings.organization.emailAddressRequestInbox && (
                                            <FormParagraph>
                                                {serviceOrganizationSettings.organization.emailAddressRequestInbox}
                                            </FormParagraph>
                                        )}
                                </>
                            )}
                        </FormGroup>
                    </>
                )}

                {!sendNotificationOrganizationInbox && (
                    <>
                        {section === 'consumer' &&
                            service.serviceType === ServiceType.examination &&
                            service.serviceConsumer && (
                                <FormGroup label={language.personalSettings.notifications.receiveAt}>
                                    {!isEditing && (
                                        <FormParagraph>
                                            {noEmailOrNotifications
                                                ? '-'
                                                : service.serviceConsumer.sendNotificationPractitionerWhenDeviant
                                                  ? language.personalSettings.notifications.sendOnDeviantResult
                                                  : language.personalSettings.notifications
                                                        .sendOnNormalAndDeviantResult}
                                        </FormParagraph>
                                    )}
                                    {isEditing && (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            gap={2}
                                            mt={[0, null, 1]}
                                        >
                                            <Radio
                                                checked={
                                                    !(serviceRelation as IServiceConsumer)
                                                        .sendNotificationPractitionerWhenDeviant
                                                }
                                                id={`${serviceRelation.id}_${section}_sendOnNormalAndDeviantResult`}
                                                label={
                                                    language.personalSettings.notifications.sendOnNormalAndDeviantResult
                                                }
                                                onChange={() => {
                                                    if (
                                                        (serviceRelation as IServiceConsumer)
                                                            .sendNotificationPractitionerWhenDeviant !== undefined
                                                    ) {
                                                        handleChange<
                                                            IServiceConsumer,
                                                            'sendNotificationPractitionerWhenDeviant'
                                                        >(
                                                            section,
                                                            serviceRelation.id,
                                                            'sendNotificationPractitionerWhenDeviant',
                                                            false
                                                        );
                                                    }
                                                }}
                                            />
                                            <Radio
                                                checked={
                                                    (serviceRelation as IServiceConsumer)
                                                        .sendNotificationPractitionerWhenDeviant
                                                }
                                                id={`${serviceRelation.id}_${section}_sendOnDeviantResult`}
                                                label={language.personalSettings.notifications.sendOnDeviantResult}
                                                onChange={() =>
                                                    handleChange<
                                                        IServiceConsumer,
                                                        'sendNotificationPractitionerWhenDeviant'
                                                    >(
                                                        section,
                                                        serviceRelation.id,
                                                        'sendNotificationPractitionerWhenDeviant',
                                                        true
                                                    )
                                                }
                                            />
                                        </Box>
                                    )}
                                </FormGroup>
                            )}
                        <FormGroup label={language.personalSettings.notifications.receiveOn}>
                            {!isEditing && (
                                <>
                                    {noEmailOrNotifications && <FormParagraph>-</FormParagraph>}
                                    {serviceRelation.sendNotificationPractitioner &&
                                        employment.practitioner.emailAddress && (
                                            <FormParagraph>{employment.practitioner.emailAddress}</FormParagraph>
                                        )}
                                    {serviceRelation.sendNotificationEmployment && employment.emailAddress && (
                                        <FormParagraph>{employment.emailAddress}</FormParagraph>
                                    )}
                                    {serviceRelation.sendNotificationEmploymentAlt && employment.emailAddressAlt && (
                                        <FormParagraph>{employment.emailAddressAlt}</FormParagraph>
                                    )}
                                </>
                            )}
                            {isEditing && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={2}
                                    mt={[0, null, 1]}
                                >
                                    {employment.practitioner.emailAddress && (
                                        <Checkbox
                                            checked={Boolean(serviceRelation.sendNotificationPractitioner)}
                                            id={`${service.id}_${section}_sendNotificationPractitioner`}
                                            label={`${employment.practitioner.emailAddress} (${language.personalSettings.notifications.personalEmail})`}
                                            onChange={() =>
                                                handleChange(
                                                    section,
                                                    serviceRelation.id,
                                                    'sendNotificationPractitioner',
                                                    !serviceRelation.sendNotificationPractitioner
                                                )
                                            }
                                        />
                                    )}
                                    {employment.emailAddress && (
                                        <Checkbox
                                            checked={Boolean(serviceRelation.sendNotificationEmployment)}
                                            id={`${service.id}_${section}_sendNotificationEmployment`}
                                            label={`${employment.emailAddress} (${language.personalSettings.notifications.workEmail})`}
                                            onChange={() =>
                                                handleChange(
                                                    section,
                                                    serviceRelation.id,
                                                    'sendNotificationEmployment',
                                                    !serviceRelation.sendNotificationEmployment
                                                )
                                            }
                                        />
                                    )}
                                    {employment.emailAddressAlt && (
                                        <Checkbox
                                            checked={Boolean(serviceRelation.sendNotificationEmploymentAlt)}
                                            id={`${service.id}_${section}_sendNotificationEmploymentAlt`}
                                            label={`${employment.emailAddressAlt} (${language.personalSettings.notifications.workEmailAlt})`}
                                            onChange={() =>
                                                handleChange(
                                                    section,
                                                    serviceRelation.id,
                                                    'sendNotificationEmploymentAlt',
                                                    !serviceRelation.sendNotificationEmploymentAlt
                                                )
                                            }
                                        />
                                    )}
                                </Box>
                            )}
                        </FormGroup>
                    </>
                )}
            </FormSection>
        );
    }

    function handleCancel() {
        setUpdatedServiceRelationIds([]);
        setIsEditing(false);
        props.onCancelled();
    }

    function handleChange<T extends IServiceConsumer | IServiceProvider | IServiceSupervisor, K extends keyof T>(
        section: Section,
        serviceRelationId: string,
        name: K,
        value: T[K]
    ) {
        if (!updatedServiceRelationIds.includes(serviceRelationId)) {
            setUpdatedServiceRelationIds((currentIds) => [...currentIds, serviceRelationId]);
        }

        switch (section) {
            case 'consumer': {
                const consumer = updatedServiceConsumers.find((c) => c.id === serviceRelationId);
                const updatedConsumer = {
                    ...consumer!,
                    [name]: value,
                };
                const newUpdatedConsumers = updatedServiceConsumers.filter((c) => c.id !== serviceRelationId);
                newUpdatedConsumers.push(updatedConsumer);
                setUpdatedServiceConsumers(newUpdatedConsumers);
                break;
            }

            case 'provider': {
                const provider = updatedServiceProviders.find((p) => p.id === serviceRelationId);
                const updatedProvider = {
                    ...provider!,
                    [name]: value,
                };
                const newUpdatedProviders = updatedServiceProviders.filter((c) => c.id !== serviceRelationId);
                newUpdatedProviders.push(updatedProvider);
                setUpdatedServiceProviders(newUpdatedProviders);
                break;
            }

            case 'supervisor': {
                const supervisor = updatedServiceSupervisors.find((s) => s.id === serviceRelationId);
                const updatedSupervisor = {
                    ...supervisor!,
                    [name]: value,
                };
                const newUpdatedSupervisors = updatedServiceSupervisors.filter((c) => c.id !== serviceRelationId);
                newUpdatedSupervisors.push(updatedSupervisor);
                setUpdatedServiceSupervisors(newUpdatedSupervisors);
                break;
            }

            default:
                never(section);
        }
    }

    async function handleSubmit() {
        setIsSaving(true);

        for (const serviceConsumer of updatedServiceConsumers.filter((updatedServiceConsumer) =>
            updatedServiceRelationIds.includes(updatedServiceConsumer.id)
        )) {
            const { item } = validate(serviceConsumerEmailNotificationPreferencesSchema, serviceConsumer);
            await abortableFetch.fetchJson(
                `/api/serviceConsumers/${serviceConsumer.id}/emailNotificationPreferences`,
                'POST',
                item
            );
        }

        for (const serviceProvider of updatedServiceProviders.filter((updatedServiceProvider) =>
            updatedServiceRelationIds.includes(updatedServiceProvider.id)
        )) {
            const { item } = validate(serviceProviderEmailNotificationPreferencesSchema, serviceProvider);
            await abortableFetch.fetchJson(
                `/api/serviceProviders/${serviceProvider.id}/emailNotificationPreferences`,
                'POST',
                item
            );
        }

        for (const serviceSupervisor of updatedServiceSupervisors.filter((updatedSupervisor) =>
            updatedServiceRelationIds.includes(updatedSupervisor.id)
        )) {
            const { item } = validate(serviceSupervisorEmailNotificationPreferencesSchema, serviceSupervisor);
            await abortableFetch.fetchJson(
                `/api/serviceSupervisors/${serviceSupervisor.id}/emailNotificationPreferences`,
                'POST',
                item
            );
        }

        setUpdatedServiceRelationIds([]);
        setIsEditing(false);
        setIsSaving(false);

        props.onSaved();
    }

    function checkNotificationSettingsForServiceRelation(
        serviceRelation: IServiceConsumer | IServiceProvider | IServiceSupervisor
    ) {
        return (
            (serviceRelation.sendNotificationEmployment && employment.emailAddress) ||
            (serviceRelation.sendNotificationEmploymentAlt && employment.emailAddressAlt) ||
            (serviceRelation.sendNotificationPractitioner && employment.practitioner.emailAddress)
        );
    }

    function getEmailSettingTitle(service: IServiceWithEmailNotificationSettings, section: Section) {
        const isGGZConsult = [
            ServiceCode.GGZCf2f,
            ServiceCode.GGZCg,
            ServiceCode.GGZCmdo,
            ServiceCode.GGZCt,
            ServiceCode.GGZCtriage,
        ].includes(service.code);
        if (!isGGZConsult) {
            return formatString(
                language.personalSettings.notifications[section][
                    service.serviceType as keyof DefaultLanguage['personalSettings']['notifications'][typeof section]
                ],
                [service.code]
            );
        }

        // GGZ consult has four different types of consult. Therefore a more specific title is needed.
        return `${language.common.result} ${service.name}`;
    }

    function initializeServiceItem(serviceItemType: 'serviceConsumer' | 'serviceProvider' | 'serviceSupervisor') {
        return props.serviceEmailNotificationSettingsForGroup.services
            .filter((service) => Boolean(service[serviceItemType]))
            .map((filteredService) => filteredService[serviceItemType]!);
    }
}
